/**
 * Filter and group key adapters for GenericStatsRow
 * For Media Devoted | Created By Ciro Autiero
 */

import type { GenericStatsRow } from '../../shared/generic-stats.types';
import type { FilterConfig } from '../../shared';
import { getVoluumValueForDisplay } from '../../shared';

export function getGenericGroupKey(row: GenericStatsRow, groupBy: string): string {
  if (groupBy === 'campaign') return row.campaign ?? '—';
  if (groupBy === 'leadsList') return row.leadsList ?? row.label ?? '—';
  if (groupBy === 'gateway') return row.gateway ?? '—';
  if (groupBy === 'gateway_leadsList') return row.gateway ?? '—';
  if (groupBy === 'leadsList_gateway') return row.leadsList ?? row.label ?? '—';
  // Reversible single-level cross trees: group by the FIRST (primary) dimension.
  if (groupBy === 'source_carrier' || groupBy === 'source_list')
    return row.source ?? '—';
  if (groupBy === 'carrier_source') return row.mobileCarrier ?? '—';
  if (groupBy === 'list_source') return row.leadsList ?? '—';
  if (groupBy === 'drop') return row.dropId ?? '—';
  if (groupBy === 'aiTextGroup') return row.aiTextGroup ?? 'Unknown text group';
  if (groupBy === 'aiTextRole') return row.aiTextRole ?? 'Unknown text prompt';
  return '';
}

/** Secondary key for nested grouping. */
export function getGenericSubGroupKey(row: GenericStatsRow, groupBy: string): string {
  if (groupBy === 'gateway_leadsList') return row.leadsList ?? row.label ?? '—';
  if (groupBy === 'leadsList_gateway') return row.gateway ?? '—';
  return '';
}

export function passesGenericFilter(
  row: GenericStatsRow,
  filter: { field: string; operator: string; value: string },
): boolean {
  const { field, operator, value } = filter;
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (field === 'tags') {
    const tags = row.performanceTags ?? [];
    const noTagsMatch =
      trimmed.toLowerCase() === '(none)' ||
      trimmed.toLowerCase() === 'no tags' ||
      trimmed.toLowerCase() === '__none__';
    if (tags.length === 0) return noTagsMatch;
    return tags.some((t) => t.toLowerCase().includes(trimmed.toLowerCase()));
  }

  if (field === 'staticTag') {
    const tagName = row._originalDrop?.drop?.staticTag?.name ?? null;
    const noTagMatch =
      trimmed.toLowerCase() === '(none)' ||
      trimmed.toLowerCase() === 'no tag' ||
      trimmed.toLowerCase() === 'sms' ||
      trimmed.toLowerCase() === '__none__';
    if (!tagName) return noTagMatch;
    return tagName.toLowerCase() === trimmed.toLowerCase();
  }

  if (
    field === 'leadList' ||
    field === 'list' ||
    field === 'gateway' ||
    field === 'source' ||
    field === 'phone' ||
    field === 'text' ||
    field === 'path' ||
    field === 'lander' ||
    field === 'offer'
  ) {
    return row.searchText.includes(trimmed.toLowerCase());
  }

  const numVal = parseFloat(trimmed);
  if (!Number.isFinite(numVal)) return true;
  const rowNum = getVoluumValueForDisplay(row.voluum, field);
  const num = typeof rowNum === 'number' ? rowNum : null;
  if (num == null || !Number.isFinite(num)) return true;

  switch (operator) {
    case '>=':
      return num >= numVal;
    case '<=':
      return num <= numVal;
    case '>':
      return num > numVal;
    case '<':
      return num < numVal;
    case '=':
      return Math.abs(num - numVal) < 1e-9;
    default:
      return true;
  }
}

/** AND logic: row passes if filtersActive is false, or all non-empty filters pass. */
export function passesAllFilters(
  row: GenericStatsRow,
  filters: FilterConfig[],
  filtersActive: boolean,
): boolean {
  if (!filtersActive) return true;
  const withValue = filters.filter((f) => f.value.trim());
  if (withValue.length === 0) return true;
  return withValue.every((f) => passesGenericFilter(row, f));
}
